import { useEffect } from 'react'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'
import { useQuery } from '@tanstack/react-query'
import APIService from 'api/APIService'
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk'
import { cleanObject } from 'utils/Functions'
import {
  IntercomSettings,
  UserArgs
} from '@intercom/messenger-js-sdk/dist/types'

const INTERCOM_APP_ID = import.meta.env.VITE_APP_INTERCOM_APP_ID
const INTERCOM_MESSENGER_ENV = import.meta.env.VITE_APP_INTERCOM_MESSENGER_ENV

const intercomSettings = {
  api_base: 'https://api-iam.intercom.io',
  app_id: INTERCOM_APP_ID,
  messenger_environment: INTERCOM_MESSENGER_ENV,
  hide_default_launcher: false
} as IntercomSettings

const useFinChatbot = () => {
  const { data: currentUser } = useUserProfile()
  const { hasTeam } = useRolesAndPermissions()
  const intercomEnabled = !!currentUser?.intercom_enabled
  const userId = currentUser?.token_id as string | undefined

  // The JWT is scoped to the user it was signed for, so the cache key must be
  // too. Otherwise a session switch reuses the previous user's token and
  // Intercom rejects it with `UserIdMismatchException`.
  const { data: intercom } = useQuery({
    queryKey: ['users', 'intercom_token', userId],
    queryFn: APIService.users.getIntercomToken,
    enabled: intercomEnabled && !!userId
  })

  // Boot once user data is ready — equivalent to mobile's loginIdentifiedUser.
  // Shut down on user change / disable so the next user boots a clean session.
  useEffect(() => {
    if (!intercomEnabled || !userId || !intercom) return

    Intercom({
      ...intercomSettings,
      intercom_user_jwt: intercom.token,
      user_id: userId
    })

    return () => shutdown()
  }, [intercomEnabled, userId, intercom])

  useEffect(() => {
    const { intercom_enabled, email, name } = currentUser || {}
    if (!intercom_enabled || !userId || !intercom) return

    const iosSubscription =
      currentUser?.subscription?.type === 'IapSubscription' ? 'Apple' : null
    const googleSubscription =
      currentUser?.subscription?.type === 'GoogleSubscription' ? 'Google' : null
    const stripeSubscription =
      currentUser?.subscription?.type === 'StripeSubscription' ? 'Stripe' : null

    const roles =
      (hasTeam && currentUser?.teams?.[0].roles?.map((role) => role.name)) ||
      null

    const customAttributes = {
      // App info
      el_auto_detect_enabled: !!currentUser?.auto_detect_enabled,
      el_auto_classify: currentUser?.auto_classify === 'ON',
      el_kms_enabled: !!currentUser?.kms_enabled,
      el_beacon_enabled: !!currentUser?.beacon_enabled,
      el_ios_app_version: currentUser?.ios_app_version || null,
      el_ios_device_version: currentUser?.ios_device_version || null,
      el_android_app_version: currentUser?.android_app_version || null,
      el_android_device_version: currentUser?.android_device_version || null,
      // General user
      el_group_as_cobrand_member:
        currentUser?.group_as_cobrand_member?.company_name || null,
      el_persona: currentUser?.persona || null,
      el_is_team_member: hasTeam,
      language_override: navigator.language,

      // B2C User
      el_subscription_status: !!currentUser?.subscription?.token_id,
      el_subscription_type:
        currentUser?.subscription?.product_identifier || null,
      el_subscription_source:
        iosSubscription || googleSubscription || stripeSubscription,
      el_referral_code_name: currentUser?.referral_code_name || null,
      el_invited_by_code: currentUser?.invited_by_code || null,
      el_trial_days_remaining: currentUser?.trial_days_remaining || null,
      el_trial_trips_remaining: currentUser?.free_trips_remaining || null,

      // B2B User
      el_role: roles,
      el_program_type: currentUser?.program?.program_type || null,
      el_variable_rate:
        currentUser?.program?.effective_rate?.miles_values?.rate || null,
      el_fixed_rate:
        currentUser?.program?.effective_rate?.miles_values?.fixed_rate || null
    }

    const cleanCustomAttributes = cleanObject(customAttributes)
    const createdAt = currentUser?.created_at
      ? Math.floor(new Date(currentUser.created_at).getTime() / 1000)
      : undefined
    const args: UserArgs = {
      ...intercomSettings,
      intercom_user_jwt: intercom.token,
      user_id: userId,
      email: email ?? undefined,
      name: name ?? undefined,
      created_at: createdAt,
      ...cleanCustomAttributes
    }

    update(args)
  }, [currentUser, userId, hasTeam, intercom])

  return null
}

export default useFinChatbot
